/**
 * Searchable dropdown for a value backed by a finite, closed value set (e.g. media type,
 * material, lighting) — reuses the same Command+Popover+ComboboxTrigger pattern as
 * MappingFieldPicker (the target-field picker in mapping-field-picker.tsx) instead of a
 * plain <Select> or free-text input, so an enum-backed field can't be mistyped
 * (CU-86can7jwy: "Fix XLS import: required column mapping dropdown for enum fields").
 *
 * Filtering mirrors MappingFieldPicker: manual, case-insensitive substring matching with
 * `shouldFilter={false}` on Command, not cmdk's built-in fuzzy filter — see that file's doc
 * comment for why fuzzy matching makes the "no results" state unreachable for a realistic
 * option count.
 */
import * as React from 'react';
import { Check } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '@ooh/ui/components/popover';
import {
  Command,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@ooh/ui/components/command';
import { cn } from '@ooh/ui/lib/utils';
import { ComboboxTrigger } from './combobox-trigger';

export interface EnumValueComboboxLabels {
  searchPlaceholder: string;
  noResultsLabel: string;
  /** Trigger label shown when `value` is empty. */
  placeholderLabel: string;
}

export interface EnumValueComboboxProps {
  value: string;
  options: readonly string[];
  onChange: (value: string) => void;
  labels: EnumValueComboboxLabels;
  testId?: string;
}

function matchesQuery(option: string, query: string): boolean {
  return option.toLowerCase().includes(query.toLowerCase());
}

export function EnumValueCombobox({
  value,
  options,
  onChange,
  labels,
  testId,
}: EnumValueComboboxProps) {
  const [open, setOpen] = React.useState(false);
  const [query, setQuery] = React.useState('');

  const visibleOptions = React.useMemo(
    () => (query ? options.filter((o) => matchesQuery(o, query)) : options),
    [options, query],
  );

  return (
    <Popover
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (!next) setQuery('');
      }}
    >
      <PopoverTrigger asChild>
        <ComboboxTrigger open={open} label={value || labels.placeholderLabel} testId={testId} />
      </PopoverTrigger>
      <PopoverContent className="w-72 p-0" align="start">
        <Command shouldFilter={false}>
          <CommandInput
            placeholder={labels.searchPlaceholder}
            value={query}
            onValueChange={setQuery}
            data-testid={testId ? `${testId}-search` : undefined}
          />
          <CommandList>
            {visibleOptions.length === 0 ? (
              // Plain element, not cmdk's <CommandEmpty> — see MappingFieldPicker's doc
              // comment for why <CommandEmpty> never renders with shouldFilter={false}.
              <div className="py-4 text-center text-sm text-muted-foreground">
                {labels.noResultsLabel}
              </div>
            ) : (
              <CommandGroup>
                {visibleOptions.map((opt) => (
                  <CommandItem
                    key={opt}
                    value={opt}
                    onSelect={() => {
                      onChange(opt);
                      setOpen(false);
                    }}
                  >
                    <Check
                      className={cn('mr-2 h-4 w-4', value === opt ? 'opacity-100' : 'opacity-0')}
                    />
                    {opt}
                  </CommandItem>
                ))}
              </CommandGroup>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
